import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Clock } from "@opendate-app/setlist";

const TICK_MS = 1000;

export const remainingLabel = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
};

const remainingParts = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  return [
    ["Hours", Math.floor(totalSeconds / 3600)],
    ["Min", Math.floor((totalSeconds % 3600) / 60)],
    ["Sec", totalSeconds % 60],
  ];
};

const WARNING_MINUTES = [1, 5, 10];

export const holdWarning = (milliseconds) => {
  const minutes = WARNING_MINUTES.find((limit) => milliseconds > 0 && milliseconds <= limit * 60 * 1000);
  if (!minutes) return "";
  return `Less than ${minutes} ${minutes === 1 ? "minute" : "minutes"} left to finish checking out.`;
};

export default function HoldCountdown({ expiresAt, variant = "claim" }) {
  const deadline = new Date(expiresAt).getTime();
  const [remaining, setRemaining] = useState(() => deadline - Date.now());

  useEffect(() => {
    const timer = setInterval(() => setRemaining(deadline - Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [deadline]);

  const warning = <span className="sr-only" role="status">{holdWarning(remaining)}</span>;

  if (remaining <= 0) {
    return <Alert color="warning" role="alert" description="Your hold on this spot has ended. Refresh the page to see where you stand." />;
  }

  if (variant === "payment") {
    return (
      <p className="pc-muted mb-0" data-testid="hold-countdown">
        {`Your spots are held for ${remainingLabel(remaining)} more while you pay. If time runs out, they're released.`}
        {warning}
      </p>
    );
  }

  return (
    <div className="pc-countdown" data-testid="hold-countdown">
      {warning}
      <span className="pc-countdown__icon" aria-hidden="true"><Clock width={22} height={22} /></span>
      <div>
        <p className="pc-countdown__title">Complete checkout to keep your spot</p>
        <p className="pc-countdown__text">{`Your spot is held for ${remainingLabel(remaining)} more. If you don't check out in time, it goes to the next person on the list and you move to the back of it.`}</p>
        <div className="pc-countdown__tiles" aria-hidden="true">
          {remainingParts(remaining).map(([label, value]) => (
            <span key={label} className="pc-countdown__tile">
              <strong>{String(value).padStart(2, "0")}</strong>
              <span>{label}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

HoldCountdown.propTypes = {
  expiresAt: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["claim", "payment"]),
};
